import React from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';

import { FeedbackContext, LocalizationContext } from '@cvt/contexts';
import { useRouter } from '@cvt/routing';

import { cacheKeys } from '../config';
import { userClient } from '../client/userClient';

export const useUserCrud = () => {
  const router = useRouter();
  const { dictionary } = React.useContext(LocalizationContext);
  const { triggerFeedback, genericErrorFeedback } = React.useContext(FeedbackContext);
  const queryClient = useQueryClient();

  const createUser = useMutation({
    mutationKey: [cacheKeys.createUser],
    mutationFn: userClient.createUser,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getUsers],
      });
      router.users.edit(data.data.id).go();
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const editUser = useMutation({
    mutationKey: [cacheKeys.editUser],
    mutationFn: ({ id, ...data }: Users.Edit) => userClient.editUser({ id, ...data }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getUsers],
      });
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getUser, {
          id: data.data.id,
        }],
      });
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const deleteUser = useMutation({
    mutationKey: [cacheKeys.deleteUser],
    mutationFn: (id: number) => userClient.deleteUser({ id }),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({
        queryKey: [cacheKeys.getUsers],
      });
      queryClient.removeQueries({
        queryKey: [cacheKeys.getUser, {
          id,
        }],
      });
      queryClient.removeQueries({
        queryKey: [cacheKeys.getUser, {
          id: id.toString(),
        }],
      });
      triggerFeedback({
        severity: 'success',
        message: dictionary.feedback.changesSaved,
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  const inviteUser = useMutation({
    mutationKey: [cacheKeys.inviteUser],
    mutationFn: userClient.inviteUser,
    onSuccess: (result, request) => {
      triggerFeedback({
        severity: 'success',
        message: dictionary.users.invite.dialog.invitationSentInfo(request.email),
      });
    },
    onError: () => {
      genericErrorFeedback();
    },
  });

  return {
    createUser: createUser.mutateAsync,
    editUser: editUser.mutateAsync,
    deleteUser: deleteUser.mutateAsync,
    inviteUser: inviteUser.mutateAsync,
  };
};
